import { useEffect, useState, type CSSProperties, type RefObject } from 'react'

interface UseFixerHeroScrollParallaxOptions {
  /** Downward translate in px as hero scrolls out */
  maxTranslatePx?: number
  /** Scale at top of hero */
  scaleFrom?: number
  /** Scale when hero has left the viewport */
  scaleTo?: number
}

function readReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Scroll parallax for fixer hero — works with nested overflow scroll containers
 * (CSS view-timeline alone is flaky inside the page scroll root).
 */
export function useFixerHeroScrollParallax(
  scrollRootRef: RefObject<HTMLElement | null>,
  heroRef: RefObject<HTMLElement | null>,
  options: UseFixerHeroScrollParallaxOptions = {}
) {
  const { maxTranslatePx = 80, scaleFrom = 1.14, scaleTo = 1.02 } = options
  const [scrollLayerStyle, setScrollLayerStyle] = useState<CSSProperties | undefined>(undefined)
  const [reducedMotion, setReducedMotion] = useState(readReducedMotion)

  useEffect(() => {
    setReducedMotion(readReducedMotion())
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReducedMotion(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    const root = scrollRootRef.current
    const hero = heroRef.current
    if (!root || !hero || reducedMotion) {
      setScrollLayerStyle(undefined)
      return
    }

    let raf = 0

    const update = () => {
      const rootRect = root.getBoundingClientRect()
      const heroRect = hero.getBoundingClientRect()
      const range = heroRect.height
      if (range <= 0) return

      const visibleTop = heroRect.top - rootRect.top
      const progress = Math.min(1, Math.max(0, -visibleTop / range))
      const scale = scaleFrom - (scaleFrom - scaleTo) * progress
      const translateY = progress * maxTranslatePx

      setScrollLayerStyle({
        transform: `translate3d(0, ${translateY}px, 0) scale(${scale})`,
        willChange: 'transform',
      })
    }

    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(update)
    }

    update()
    root.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      cancelAnimationFrame(raf)
      root.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [scrollRootRef, heroRef, reducedMotion, maxTranslatePx, scaleFrom, scaleTo])

  return { scrollLayerStyle, reducedMotion }
}
